import {MapContainer, TileLayer, Marker, Popup, Polygon, Rectangle } from 'react-leaflet';
import 'leaflet.fullscreen';
import 'leaflet.fullscreen/dist/Control.FullScreen.css';
import 'leaflet/dist/leaflet.css';
import CustomGenericTittleIconDataChip from './../../../../../components/CustomGenericTittleIconDataChip';
import { MarkerTitle } from '../../../../../components/buttons/IconButtons';
import { DRAW_MODE } from '../Model/Model';
import { colors } from '../../../../../enums/colors';
import GeoFencesMapApp from '../Functionality/GeoFencesMapApp';
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from "../../../../../Apis/functions";
import {Box} from "@mui/material";
import {MapEventHandler} from "../../../../../components/Leaflet/MapFunctions";
import {DEFAULT_MARKER_ICON} from "../../../../../components/Leaflet/MarkerIcons";

/**
 *
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTitle: Frontend
 * @Description: React component to render a Leaflet map with polygons or rectangles
 *               representing geogeofences, and optional markers for locations.
 *
 * @param {Array} center - Coordinates to center the map [latitude, longitude] (optional, default: [20.5888, -100.3899])
 * @param {Array} locations - Array of location objects to display as markers.
 * @param {Array} geofences - Array of geogeofence objects to display as polygons or rectangles.
 * @param {string} geofenceTitle - Title of the map.
 * @param {boolean} showTitle - Whether to show the title section.
 * @param {string} [className="mb-4"] - Extra CSS class for the main container. Default is "mb-4".
 * @param {number} [zoom=15] - Initial zoom level of the map.
 * @param {number} [marginBottom=120]
 * @returns {JSX.Element}
 */

const GeofencesMap = ({
    center = [DEFAULT_LATITUDE, DEFAULT_LONGITUDE],
    locations = [],
    geofences = [],
    geofenceTitle = 'map',
    showTitle = true,
    classNameMap = null,
    key="mpk_gm_map_",
    onMarkerPlaced = null,
    disabled = false,
    emptyFields=false,
    marginBottom=120,
    zoom= 10,
    onMapMove
}) => {

    const {
        tileUrls,
        mapType,
        elementRefMap,
        height,
        MapClickHandler,
        DisabledMap,
        CardEmptyFields
    } = GeoFencesMapApp({
        onMarkerPlaced,
        geofences,
        classNameMap,
        marginBottom
    });

    return (
        <div className={"w-100 h-100 border-full-shades-100 border-radius-8px position-relative"} ref={elementRefMap}>
            {showTitle && (
                <div className={`${showTitle ? 'mt-3' : ''}`} >
                    <CustomGenericTittleIconDataChip Icon={MarkerTitle} title={geofenceTitle} border={false} />
                </div>
            )}
            {disabled === true ?
                <DisabledMap
                    disabled={disabled}
                    className={classNameMap}
                />
                :
                <>
                    {(classNameMap !== null || height !== null) &&
                    <Box sx={height !== null ? { height: height } : undefined} className={classNameMap}>
                        <MapContainer
                            center={center}
                            zoom={zoom}
                            minZoom={3}
                            className='w-100 h-100'
                            fullscreenControl={true}
                        >

                            <TileLayer
                                key={mapType}
                                url={tileUrls[mapType]}
                            />
                            <MapEventHandler onMapMove={onMapMove} />

                            {locations.map((loc) => (
                                <Marker key={loc?.id || key} position={loc.position} icon={DEFAULT_MARKER_ICON}>
                                    {(loc.name || loc.phone || loc.address) &&
                                        <Popup>
                                            <div>
                                                <p>
                                                    <b>{loc?.name || ""}</b>
                                                </p>
                                                <p>{loc?.phone || ""}</p>
                                                <p>{loc?.address || ""}</p>
                                            </div>
                                        </Popup>
                                    }
                                </Marker>
                            ))}
                            {onMarkerPlaced && <MapClickHandler /> }
                            {(geofences || []).map((geofence, index) => {
                                const positions = geofence.coordinates;

                                const pathOptions = {
                                    color: geofence.strokeColor || colors.secondary700,
                                    weight: Number(geofence.strokeWidth) || 2,
                                    fillColor: geofence.fillColor || colors.secondary700,
                                    fillOpacity: 0.4,
                                };
                                if (geofence.type === DRAW_MODE.RECTANGLE) {
                                    return (
                                        <Rectangle
                                            key={`${geofence.id}-${index}`}
                                            bounds={positions}
                                            pathOptions={pathOptions}
                                        />
                                    );
                                }

                                return (
                                    <Polygon key={`${geofence.id}-${index}`} positions={positions} pathOptions={pathOptions} />
                                );
                            })}
                        </MapContainer>

                        { emptyFields && <CardEmptyFields/>}
                    </Box>
                    }
                </>
            }
        </div>
    );
};

export default GeofencesMap;
